<template>
  <v-menu v-model="menuOpen" :close-on-content-click="false" location="bottom start" offset="4">
    <template #activator="{ props: menuProps }">
      <v-badge :model-value="activeCount > 0" :content="activeCount" color="primary" offset-x="4" offset-y="4">
        <v-btn v-bind="menuProps" variant="tonal" color="primary" size="small" prepend-icon="fa-filter">
          {{ translate('parcours.student.filter.students') }}
        </v-btn>
      </v-badge>
    </template>

    <v-card min-width="360" max-width="420">
      <v-card-text class="pb-2" @keydown.enter="apply">
        <v-text-field v-model="draft.nameContains" :label="translate('parcours.student.filter.nameContains')" density="compact" hide-details
          clearable class="mb-3" />
        <v-text-field v-model="draft.emailContains" :label="translate('parcours.student.filter.emailContains')" density="compact" hide-details
          clearable class="mb-3" />
        <v-text-field v-model="draft.recordNumberContains" :label="translate('parcours.student.filter.recordNumberContains')" density="compact"
          hide-details clearable class="mb-3" />

        <div class="d-flex ga-2 mb-3">
          <v-text-field v-model.number="draft.ageMin" :label="translate('parcours.student.filter.ageFrom')" type="number" min="0" density="compact"
            hide-details clearable />
          <v-text-field v-model.number="draft.ageMax" :label="translate('parcours.student.filter.ageTo')" type="number" min="0" density="compact"
            hide-details clearable />
        </div>

        <DateField v-model="birthDateModel" :label="translate('parcours.student.filter.birthDateExact')" density="compact" hide-details
          clearable class="mb-3" />

        <v-autocomplete v-model="draft.pathTypes" :items="pathTypeOptions" item-title="label" item-value="value"
          :label="translate('parcours.student.filter.pathType')" density="compact" hide-details multiple chips closable-chips clearable
          class="mb-3" />
        <v-autocomplete v-model="draft.exemptionCodes" :items="exemptionCodeOptions" item-title="label"
          item-value="value" :label="translate('parcours.student.filter.exemptionCode')" density="compact" hide-details multiple chips closable-chips
          clearable class="mb-3" />
        <v-autocomplete v-model="draft.fundingSources" :items="fundingSourceOptions" item-title="label"
          item-value="value" :label="translate('parcours.student.filter.fundingSource')" density="compact" hide-details multiple chips
          closable-chips clearable />
      </v-card-text>
      <v-card-actions>
        <v-btn variant="text" size="small" @click="reset">{{ translate('parcours.student.filter.reset') }}</v-btn>
        <v-spacer />
        <v-btn variant="text" size="small" @click="menuOpen = false">{{ translate('parcours.student.filter.cancel') }}</v-btn>
        <v-btn color="primary" size="small" @click="apply">{{ translate('parcours.student.filter.apply') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { translate } from '@fondationcss/sdk';
import DateField from '@/components/date-field.vue';
import { getFundingSourceOptions, getPathTypeOptions, getExemptionCodeOptions } from '@/utils/frequentation-codes';
import {
  type StudentFilterCriteria,
  emptyStudentFilter,
  normalizeStudentFilter,
  studentFilterCount,
} from '@/utils/student-filter';

const props = defineProps<{
  modelValue: StudentFilterCriteria;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: StudentFilterCriteria];
}>();

const menuOpen = ref(false);
const draft = ref<StudentFilterCriteria>(cloneFromModel());

const fundingSourceOptions = computed(() => getFundingSourceOptions());
const pathTypeOptions = computed(() => getPathTypeOptions());
const exemptionCodeOptions = computed(() => getExemptionCodeOptions());

const activeCount = computed(() => studentFilterCount(props.modelValue));

// DateField works with `string | null`; the criteria use `string | undefined`.
const birthDateModel = computed<string | null>({
  get: () => draft.value.birthDate ?? null,
  set: (v) => { draft.value.birthDate = v ?? undefined; },
});

function cloneFromModel(): StudentFilterCriteria {
  const m = props.modelValue ?? {};
  return {
    ...emptyStudentFilter(),
    ...m,
    // Copy arrays so edits to the draft don't mutate the committed value.
    pathTypes: [...(m.pathTypes ?? [])],
    exemptionCodes: [...(m.exemptionCodes ?? [])],
    fundingSources: [...(m.fundingSources ?? [])],
  };
}

// Refresh the draft from the committed value whenever the menu opens, so an
// abandoned edit (Annuler / click-away) is discarded.
watch(menuOpen, (open) => {
  if (open) draft.value = cloneFromModel();
});

function apply() {
  emit('update:modelValue', normalizeStudentFilter(draft.value) ?? emptyStudentFilter());
  menuOpen.value = false;
}

function reset() {
  draft.value = emptyStudentFilter();
  emit('update:modelValue', emptyStudentFilter());
  menuOpen.value = false;
}
</script>
